import { html, css, nothing } from 'lit';
import type { PropertyValues } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { msg, localized } from '@lit/localize';
import { SsoElement } from '../components/sso-base.js';
import { endpoints } from '../api/client.js';
import { navigate } from '../router.js';
import '../components/sso-card.js';
import '../components/sso-button.js';
import '../components/sso-alert.js';
import '../components/sso-spinner.js';

/**
 * `<enrollment-gate-page>` — forced 2FA enrollment when policy requires it and the user has none
 * (ARCHITECTURE C4, SSO-MOCKUPS.md §6).
 *
 * Passkey is recommended (top); TOTP is always available as the accessible fallback. Within the
 * grace period a "skip for now (N days left)" action and a warning banner appear; once grace has
 * passed the only escape is signing out.
 */
@customElement('enrollment-gate-page')
@localized()
export class EnrollmentGatePage extends SsoElement {
  static override styles = [
    SsoElement.styles,
    css`
      .methods {
        display: grid;
        gap: 0.75rem;
        margin: var(--sso-space) 0;
      }
      button.method {
        display: flex;
        align-items: center;
        gap: 0.9rem;
        width: 100%;
        text-align: start;
        padding: 0.9rem 1rem;
        min-height: var(--sso-touch-target);
        background: var(--sso-color-surface);
        border: 1px solid var(--sso-color-border);
        border-radius: var(--sso-radius);
        cursor: pointer;
        color: var(--sso-color-fg);
        font: inherit;
      }
      button.method:hover {
        border-color: var(--sso-color-brand);
      }
      button.method:focus-visible {
        outline: none;
        box-shadow: var(--sso-focus-ring);
      }
      .ico {
        font-size: 1.6rem;
        flex: none;
      }
      .meta {
        flex: 1;
      }
      .meta .title {
        font-weight: 600;
      }
      .meta .desc {
        color: var(--sso-color-muted);
        font-size: 0.9rem;
      }
      .footer {
        display: grid;
        gap: 0.75rem;
        margin-top: var(--sso-space);
      }
      .signout {
        text-align: center;
        font-size: 0.9rem;
      }
    `,
  ];

  @state() private ready = false;
  @state() private error = '';

  override async connectedCallback(): Promise<void> {
    super.connectedCallback();
    try {
      // If the user already has an active 2FA method, there is nothing to gate — follow the app-initiated
      // return target if any, else go to the launcher.
      const session = await endpoints.session();
      if (session.mfaEnrolled) {
        const returnUri = this.resolveReturnUri();
        if (returnUri) { this.redirect(returnUri); return; }
        navigate('/');
        return;
      }
      this.ready = true;
    } catch {
      this.error = msg('Could not load your security settings. Please try again.');
    }
  }

  override updated(changed: PropertyValues): void {
    super.updated(changed);
    // Content loads asynchronously — focus the first method button once it has rendered.
    this.focusFirstControl();
  }

  private renderMethod(kind: 'passkey' | 'totp') {
    const isPasskey = kind === 'passkey';
    return html`
      <button
        class="method"
        type="button"
        @click=${() => navigate(isPasskey ? '/enroll/passkey' : '/enroll/totp')}>
        <span class="ico" aria-hidden="true">${isPasskey ? '🔑' : '🔢'}</span>
        <span class="meta">
          <span class="title">
            ${isPasskey ? msg('Passkey (recommended)') : msg('Authenticator app')}
          </span>
          <span class="desc">
            ${isPasskey ? msg('Fastest and most secure') : msg('Works on any device')}
          </span>
        </span>
        <span aria-hidden="true">›</span>
      </button>
    `;
  }

  override render() {
    if (!this.ready && !this.error) {
      return html`<sso-card><sso-spinner aria-label=${msg('Loading')}></sso-spinner></sso-card>`;
    }
    return html`
      <sso-card>
        <h1>${msg('Set up two-factor authentication')}</h1>
        <p>
          ${msg(
            'Your account requires a second sign-in step. Choose how you want to protect it. You can add a second method later.',
          )}
        </p>
        ${this.error ? html`<sso-alert kind="error">${this.error}</sso-alert>` : nothing}

        <div class="methods">
          ${this.renderMethod('passkey')}
          ${this.renderMethod('totp')}
        </div>

        <sso-alert kind="warning">${msg('You cannot continue until you set this up.')}</sso-alert>

        <div class="footer">
          <p class="signout"><a href="/logout">${msg('Sign out')}</a></p>
        </div>
      </sso-card>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'enrollment-gate-page': EnrollmentGatePage;
  }
}
